<template>
  <div class="smart-rec-layout bg-light min-vh-100 d-flex flex-column">
    <Header />
    
    <main class="flex-grow-1 py-4 py-md-5">
      <div class="container">
        
        <!-- Phần Form (Nhỏ gọn, thu vào giữa) -->
        <div class="row justify-content-center">
          <div class="col-lg-10 col-xl-8">
            <div class="text-center mb-4">
              <h2 class="fw-bold text-dark mb-2" style="font-family: serif;">Đề xuất thông minh 🪄</h2>
              <p class="text-muted small">Hãy để trí tuệ nhân tạo tìm kiếm 3 cuốn sách phù hợp nhất với trạng thái của bạn lúc này.</p>
            </div>

            <div class="card border-0 shadow-sm rounded-4 p-3 p-md-4 mb-5">
              <form @submit.prevent="handleSuggest">
                
                <!-- 1. Bạn đang nghĩ gì -->
                <div class="mb-4">
                  <label class="form-label fw-bold text-dark small mb-1">Bạn đang nghĩ gì hôm nay?</label>
                  <textarea
                    v-model="prefFreeText"
                    class="form-control bg-light border-0"
                    placeholder="Ví dụ: Mình đang cảm thấy hơi mệt mỏi, cần một cuốn sách nhẹ nhàng để chữa lành tâm hồn..."
                    rows="2"
                    maxlength="300"
                    style="resize: none; font-size: 0.9rem;"
                  ></textarea>
                </div>

                <!-- 2. Thể loại -->
                <div class="mb-4">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <label class="form-label fw-bold text-dark small mb-0">Thể loại yêu thích</label>
                    <span class="badge bg-secondary rounded-pill" style="font-size: 0.7rem;">{{ prefGenres.length }} / 3</span>
                  </div>

                  <div v-if="selectedGenres.length > 0" class="d-flex flex-wrap gap-2 mb-2">
                    <label 
                      v-for="opt in selectedGenres" 
                      :key="'sel-' + opt.value"
                      class="btn btn-sm rounded-pill border btn-dark"
                    >
                      <input type="checkbox" :value="opt.value" v-model="prefGenres" class="d-none" />
                      <icon :icon="opt.icon" class="me-1" style="font-size: 0.7rem;" />
                      <span style="font-size: 0.8rem;">{{ opt.label }}</span>
                      <icon icon="fa-solid fa-xmark" class="ms-2" style="font-size: 0.8rem; cursor: pointer;" />
                    </label>
                  </div>

                  <input v-model="searchGenreQuery" type="text" class="form-control form-control-sm bg-light border-0 mb-2" placeholder="Tìm nhanh thể loại..."/>
                  
                  <div class="d-flex flex-wrap gap-2" style="max-height: 120px; overflow-y: auto;">
                    <label 
                      v-for="opt in filteredGenreOptions" 
                      :key="opt.value"
                      class="btn btn-sm rounded-pill border"
                      :class="prefGenres.includes(opt.value) ? 'btn-dark' : 'btn-white text-muted'"
                      :style="prefGenres.length >= 3 && !prefGenres.includes(opt.value) ? 'opacity: 0.5; pointer-events: none;' : ''"
                    >
                      <input type="checkbox" :value="opt.value" v-model="prefGenres" class="d-none" />
                      <icon :icon="opt.icon" class="me-1" style="font-size: 0.7rem;" />
                      <span style="font-size: 0.8rem;">{{ opt.label }}</span>
                    </label>
                  </div>
                </div>

                <!-- 3. Grid 3 cột nhỏ gọn -->
                <div class="row g-3 mb-4">
                  
                  <!-- Tâm trạng -->
                  <div class="col-md-4">
                    <label class="form-label fw-bold text-dark small mb-2">Tâm trạng 🌀</label>
                    <div class="d-flex flex-column gap-2">
                      <label 
                        v-for="opt in moodOptions" 
                        :key="opt.value" 
                        class="compact-radio-card"
                        :class="{'active': prefMood === opt.value}"
                      >
                        <input type="radio" :value="opt.value" v-model="prefMood" class="d-none" />
                        <span class="me-2">{{ opt.emoji }}</span>
                        <span class="small">{{ opt.label }}</span>
                      </label>
                    </div>
                  </div>

                  <!-- Phong cách -->
                  <div class="col-md-4">
                    <label class="form-label fw-bold text-dark small mb-2">Phong cách 🏆</label>
                    <div class="d-flex flex-column gap-2">
                      <label 
                        v-for="opt in bookTypeOptions" 
                        :key="opt.value" 
                        class="compact-radio-card"
                        :class="{'active': prefBookType === opt.value}"
                      >
                        <input type="radio" :value="opt.value" v-model="prefBookType" class="d-none" />
                        <span class="me-2">{{ opt.emoji }}</span>
                        <span class="small">{{ opt.label }}</span>
                      </label>
                    </div>
                  </div>

                  <!-- Mục tiêu -->
                  <div class="col-md-4">
                    <label class="form-label fw-bold text-dark small mb-2">Mục tiêu 🚀</label>
                    <div class="d-flex flex-column gap-2">
                      <label 
                        v-for="opt in goalOptions" 
                        :key="opt.value" 
                        class="compact-radio-card"
                        :class="{'active': prefGoal === opt.value}"
                      >
                        <input type="radio" :value="opt.value" v-model="prefGoal" class="d-none" />
                        <span class="me-2">{{ opt.emoji }}</span>
                        <span class="small">{{ opt.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Nút Submit -->
                <div class="text-center mt-2 border-top pt-4">
                  <button type="submit" class="btn btn-dark rounded-pill px-5 py-2 fw-bold" :disabled="isLoading">
                    <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
                    <icon v-else icon="fa-solid fa-wand-magic-sparkles" class="me-2" />
                    {{ isLoading ? 'AI Đang tìm kiếm...' : 'Khám phá ngay' }}
                  </button>
                </div>
                
              </form>
            </div>
          </div>
        </div>

        <!-- Kết quả (3 cuốn sách nằm ngang nhau) -->
        <div v-if="suggestedBooks && suggestedBooks.length > 0" class="row justify-content-center">
          <div class="col-12 col-xl-11">
            <div class="text-center mb-4">
              <div class="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill bg-dark text-white small fw-bold mb-2 shadow-sm">
                <icon icon="fa-solid fa-wand-magic-sparkles" style="font-size: 0.75rem;" />
                <span>Kết quả phân tích AI</span>
              </div>
              <h3 class="fw-bold text-dark mb-1" style="font-family: serif;">Sách dành riêng cho bạn</h3>
              <p class="text-muted small">3 cuốn sách được tuyển chọn phù hợp nhất với tâm trạng và sở thích của bạn</p>
            </div>

            <!-- Grid 3 cuốn sách nằm ngang nhau -->
            <div class="smart-rec-results-grid">
              <div 
                v-for="(book, idx) in suggestedBooks" 
                :key="book.id || idx"
                class="smart-rec-result-col"
              >
                <div class="smart-rec-card-container h-100 d-flex flex-column">
                  <div class="flex-grow-1">
                    <BookCard 
                      :item="book" 
                      mode="catalog" 
                      @click="handleCardClick(book.id)" 
                    />
                  </div>

                  <!-- Lý do AI gợi ý (Desktop: hiện đầy đủ; iPad/iPhone: hiện nút Xem thêm mở Popup) -->
                  <div v-if="book.aiReason" class="smart-rec-reason-box mt-2">
                    <div class="d-flex align-items-start gap-1">
                      <icon icon="fa-solid fa-quote-left" class="text-primary flex-shrink-0 mt-1" style="font-size: 0.75rem;" />
                      <div class="flex-grow-1 min-w-0">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                          <span class="fw-bold text-dark" style="font-size: 0.76rem;">Lý do gợi ý:</span>
                          <!-- Nút Xem thêm: chỉ hiển thị trên iPad và Mobile (< 992px) -->
                          <button
                            v-if="book.aiReason && book.aiReason.length > 30"
                            type="button"
                            class="btn-toggle-reason d-lg-none"
                            @click.stop="openReasonModal(book)"
                          >
                            <span>Xem thêm</span>
                            <icon icon="fa-solid fa-arrow-up-right-from-square" class="ms-1" style="font-size: 0.65rem;" />
                          </button>
                        </div>
                        <!-- Nội dung: Desktop hiển thị toàn bộ, iPad/Mobile rút gọn 2 dòng -->
                        <p class="smart-rec-reason-text m-0">
                          {{ book.aiReason }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- MODAL POPUP LÝ DO GỢI Ý (iPad & Mobile) -->
        <div 
          v-if="selectedBookForModal" 
          class="smart-rec-modal-overlay" 
          @click.self="closeReasonModal"
        >
          <div class="smart-rec-modal-dialog">
            <div class="smart-rec-modal-content">
              <!-- Header -->
              <div class="smart-rec-modal-header">
                <div class="d-flex align-items-center gap-2">
                  <div class="smart-rec-modal-icon">
                    <icon icon="fa-solid fa-wand-magic-sparkles" />
                  </div>
                  <div>
                    <h6 class="m-0 fw-bold text-dark">Lý do gợi ý từ AI</h6>
                    <small class="text-muted">Phân tích chuyên sâu theo gu đọc</small>
                  </div>
                </div>
                <button type="button" class="btn-modal-close" @click="closeReasonModal" title="Đóng">
                  <icon icon="fa-solid fa-xmark" />
                </button>
              </div>

              <!-- Body -->
              <div class="smart-rec-modal-body">
                <!-- Book Info snippet -->
                <div class="smart-rec-modal-book-preview d-flex align-items-center gap-3 mb-3 p-2 rounded-3">
                  <img 
                    v-if="selectedBookForModal.cover" 
                    :src="selectedBookForModal.cover" 
                    :alt="selectedBookForModal.name" 
                    class="modal-book-cover"
                  />
                  <div class="min-w-0">
                    <h6 class="fw-bold text-dark text-truncate mb-1" style="font-size: 0.95rem;">
                      {{ selectedBookForModal.name }}
                    </h6>
                    <p class="text-muted small m-0 text-truncate" v-if="selectedBookForModal.authorName || selectedBookForModal.author">
                      <icon icon="fa-solid fa-feather-pointed" class="me-1 opacity-75" />
                      {{ selectedBookForModal.authorName || selectedBookForModal.author }}
                    </p>
                  </div>
                </div>

                <!-- Full Reason Quote Box -->
                <div class="smart-rec-modal-quote-box p-3 rounded-3">
                  <div class="d-flex align-items-start gap-2">
                    <icon icon="fa-solid fa-quote-left" class="text-primary mt-1 flex-shrink-0" style="font-size: 1.1rem;" />
                    <p class="m-0 text-dark" style="font-size: 0.92rem; line-height: 1.6;">
                      {{ selectedBookForModal.aiReason }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="smart-rec-modal-footer d-flex justify-content-end gap-2">
                <button type="button" class="btn btn-sm btn-outline-secondary rounded-pill px-3" @click="closeReasonModal">
                  Đóng
                </button>
                <button 
                  type="button" 
                  class="btn btn-sm btn-dark rounded-pill px-3" 
                  @click="handleCardClick(selectedBookForModal.id); closeReasonModal();"
                >
                  Xem chi tiết sách <icon icon="fa-solid fa-arrow-right" class="ms-1" />
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </main>
    <Footer />
  </div>
</template>

<script src="./smart-recommendation.js"></script>
<style scoped>
.smart-rec-layout {
  background-color: #f4f6f8;
}

.btn-white {
  background-color: #fff;
  border-color: #dee2e6;
}
.btn-white:hover {
  background-color: #f8f9fa;
}

.compact-radio-card {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.compact-radio-card:hover {
  border-color: #adb5bd;
  background: #fff;
}

.compact-radio-card.active {
  background: #212529;
  border-color: #212529;
  color: #fff;
}

/* 3 cuốn sách luôn nằm ngang nhau */
.smart-rec-results-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  width: 100%;
  box-sizing: border-box;
}

.smart-rec-result-col {
  min-width: 0;
  width: 100%;
}

.smart-rec-card-container {
  position: relative;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.smart-rec-card-container:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08);
  border-color: #cbd5e1;
}

.smart-rec-reason-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 10px;
  transition: all 0.2s ease;
}

/* Desktop: Hiển thị đầy đủ chữ không cắt bớt */
.smart-rec-reason-text {
  font-size: 0.78rem;
  line-height: 1.45;
  color: #4b5563;
  transition: all 0.2s ease;
  word-break: break-word;
  display: block;
}

.btn-toggle-reason {
  display: inline-flex;
  align-items: center;
  background: transparent;
  border: none;
  padding: 0;
  color: #2563eb;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.15s ease;
}

.btn-toggle-reason:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

/* ==========================================================================
   MODAL POPUP (iPad & iPhone)
   ========================================================================== */
.smart-rec-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(5px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: fadeIn 0.2s ease-out;
}

.smart-rec-modal-dialog {
  width: 100%;
  max-width: 480px;
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: modalPop 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.smart-rec-modal-content {
  padding: 20px 24px;
}

.smart-rec-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}

.smart-rec-modal-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
}

.btn-modal-close {
  background: transparent;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  transition: all 0.15s ease;
  cursor: pointer;
}

.btn-modal-close:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.smart-rec-modal-body {
  padding: 16px 0;
}

.smart-rec-modal-book-preview {
  background: #f8fafc;
  border: 1px solid #f1f5f9;
}

.modal-book-cover {
  width: 48px;
  height: 68px;
  object-fit: cover;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
}

.smart-rec-modal-quote-box {
  background: #eff6ff;
  border: 1px solid #dbeafe;
}

.smart-rec-modal-footer {
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes modalPop {
  from { opacity: 0; transform: scale(0.92) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

/* ==========================================================================
   RESPONSIVE: iPad (< 992px) & Mobile (< 576px)
   ========================================================================== */
@media (max-width: 991.98px) {
  /* iPad & iPhone: Cắt bớt 2 dòng và hiện nút Xem thêm */
  .smart-rec-reason-text {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

@media (max-width: 767.98px) {
  .smart-rec-results-grid {
    gap: 0.75rem;
  }
  .smart-rec-card-container {
    padding: 10px;
    border-radius: 12px;
  }
}

@media (max-width: 575.98px) {
  .smart-rec-results-grid {
    gap: 0.5rem;
  }
  .smart-rec-card-container {
    padding: 8px;
    border-radius: 10px;
  }
  .smart-rec-reason-box {
    padding: 6px 8px;
  }
  .smart-rec-reason-text {
    font-size: 0.72rem;
  }
  .btn-toggle-reason {
    font-size: 0.68rem;
  }
  .smart-rec-modal-content {
    padding: 16px 18px;
  }
}
</style>
